import React, { useState, useEffect } from 'react';
import { FireIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GasPod, CreateGasPodData, GasMeasurementType, GasMeterType, GasQuality } from '../../types/gas';
import { gasPodService } from '../../services/gasService';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface GasPodsSectionProps {
  customerId: string;
}

const GasPodsSection: React.FC<GasPodsSectionProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [pods, setPods] = useState<GasPod[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingPodId, setEditingPodId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CreateGasPodData>>({
    podId: '',
    name: '',
    measurementType: 'AMR',
    status: 'active',
    gasQuality: 'H',
    address: {
      postalCode: '',
      city: '',
      street: '',
      houseNumber: '',
      plotNumber: ''
    }
  });

  const loadPods = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const data = await gasPodService.getGasPodsByCustomerId(customerId);
      setPods(data);
    } catch (error) {
      console.error('Error loading gas pods:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.pods') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPods();
  }, [customerId]);

  const handleAdd = async () => {
    if (!formData.podId?.trim()) {
      toast.error(t('gas.pod.errors.podIdRequired'));
      return;
    }
    if (!formData.address?.postalCode?.trim() || !formData.address?.city?.trim() || !formData.address?.street?.trim()) {
      toast.error(t('gas.pod.errors.addressRequired'));
      return;
    }

    try {
      const payload = {
        podId: formData.podId.trim(),
        name: formData.name?.trim(),
        measurementType: formData.measurementType || 'AMR',
        status: formData.status || 'active',
        gasQuality: formData.gasQuality || 'H',
        address: {
          postalCode: formData.address.postalCode.trim(),
          city: formData.address.city.trim(),
          street: formData.address.street.trim(),
          houseNumber: formData.address.houseNumber?.trim(),
          plotNumber: formData.address.plotNumber?.trim()
        },
        meterSerialNumber: formData.meterSerialNumber?.trim(),
        meterType: formData.meterType,
        capacityKw: formData.capacityKw,
        maxHourlyCapacityM3: formData.maxHourlyCapacityM3
      };
      if (editingPodId) {
        await gasPodService.updateGasPod(editingPodId, payload);
        toast.success(t('gas.messages.saveSuccess', { name: t('gas.pod.name') }));
      } else {
        await gasPodService.createGasPod({ customerId, ...payload });
        toast.success(t('gas.messages.addSuccess', { name: t('gas.pod.name') }));
      }
      setShowAdd(false);
      setEditingPodId(null);
      setFormData({
        podId: '',
        name: '',
        measurementType: 'AMR',
        status: 'active',
        gasQuality: 'H',
        address: {
          postalCode: '',
          city: '',
          street: '',
          houseNumber: '',
          plotNumber: ''
        }
      });
      loadPods();
    } catch (error) {
      console.error('Error creating gas pod:', error);
      toast.error(t('gas.messages.addError', { name: t('gas.pod.name') }));
    }
  };

  const handleEdit = (pod: GasPod) => {
    setFormData({
      podId: pod.podId,
      name: pod.name || '',
      measurementType: pod.measurementType,
      status: pod.status,
      gasQuality: pod.gasQuality,
      address: {
        postalCode: pod.address.postalCode || '',
        city: pod.address.city || '',
        street: pod.address.street || '',
        houseNumber: pod.address.houseNumber || '',
        plotNumber: pod.address.plotNumber || ''
      },
      meterSerialNumber: pod.meterSerialNumber || '',
      meterType: pod.meterType,
      capacityKw: pod.capacityKw,
      maxHourlyCapacityM3: pod.maxHourlyCapacityM3
    });
    setEditingPodId(pod.id);
    setShowAdd(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('gas.messages.confirmDelete'))) return;
    try {
      await gasPodService.deleteGasPod(id);
      toast.success(t('gas.messages.deleteSuccess', { name: t('gas.pod.name') }));
      loadPods();
    } catch (error) {
      console.error('Error deleting gas pod:', error);
      toast.error(t('gas.messages.deleteError', { name: t('gas.pod.name') }));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FireIcon className="h-6 w-6 text-orange-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('gas.sections.pods')}</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          {showAdd ? t('gas.common.cancel') : t('gas.common.new')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('gas.common.loading')}</p>
      ) : pods.length === 0 ? (
        <p className="text-sm text-gray-500">{t('gas.common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {pods.map(pod => (
            <div key={pod.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="font-mono text-sm font-semibold text-gray-900">{pod.podId}</p>
                <p className="text-xs text-gray-500">
                  {pod.address.postalCode} {pod.address.city}, {pod.address.street} {pod.address.houseNumber}
                </p>
                <div className="flex gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">{pod.measurementType}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">{pod.gasQuality === 'H' ? 'H gáz' : 'L gáz'}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">{pod.status}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => handleEdit(pod)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('gas.common.edit')}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(pod.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('gas.common.delete')}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
          <h3 className="text-sm font-semibold text-orange-900 mb-2">{t('gas.sections.pods')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.id')}</label>
              <input
                type="text"
                value={formData.podId || ''}
                onChange={e => setFormData({ ...formData, podId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder={t('gas.pod.fields.placeholder.podId')}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.name')}</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder={t('gas.pod.fields.placeholder.name')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.postalCode')}</label>
              <input
                type="text"
                value={formData.address?.postalCode || ''}
                onChange={e => setFormData({ ...formData, address: { ...formData.address, postalCode: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.city')}</label>
              <input
                type="text"
                value={formData.address?.city || ''}
                onChange={e => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.street')}</label>
              <input
                type="text"
                value={formData.address?.street || ''}
                onChange={e => setFormData({ ...formData, address: { ...formData.address, street: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.measurementType')}</label>
              <select
                value={formData.measurementType || 'AMR'}
                onChange={e => setFormData({ ...formData, measurementType: e.target.value as GasMeasurementType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="AMR">AMR</option>
                <option value="SLP">SLP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.gasQuality')}</label>
              <select
                value={formData.gasQuality || 'H'}
                onChange={e => setFormData({ ...formData, gasQuality: e.target.value as GasQuality })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="H">H gáz</option>
                <option value="L">L gáz</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.meterSerialNumber')}</label>
              <input
                type="text"
                value={formData.meterSerialNumber || ''}
                onChange={e => setFormData({ ...formData, meterSerialNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.pod.fields.meterType')}</label>
              <select
                value={formData.meterType || ''}
                onChange={e => setFormData({ ...formData, meterType: e.target.value as GasMeterType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">-</option>
                <option value="diaphragm">Membrán</option>
                <option value="rotary">Forgó</option>
                <option value="turbine">Turbina</option>
                <option value="ultrasonic">Ultrahangos</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              {t('gas.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700"
            >
              {t('gas.common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GasPodsSection;
